/**
 * The channel:command render contract — the ONE thing the client renders from,
 * derived straight from the locked oracle (world-graph/sci/v1/schema/graph.py).
 *
 * The client IS the `channel:type:ws` channel. Everything on screen is an
 * Announcement; an Announcement carries a `render` LIST (sci//render:@file**)
 * — one entry per box (page:@box). The WORLD owns every contract value (the
 * design css params, the artifact definitions); the client only REFS them.
 *
 *   machine   → sci//machine:* — the universal identity + customization bundle
 *               (name/id/label/description/docs). label/docs are how anything is
 *               customized; docs is the intent / "any meaning to it" pass-through.
 *   serial    → sci//serial:index — ordering inside a sync batch.
 *   position  → a file suffix selects one shape:type. A deterministic
 *               address uses that grammar; a dehydrated artifact position
 *               also carries its shape machine and evidence references.
 *               A file's position is never overridden.
 *   design    → sci//design:css:@machine + mode:{dark,light} — world-owned css
 *               params, default shared by type, customizable per machine. The
 *               client refs a machine; it does NOT own the values.
 *   render    → sci//render:@file** — embed @file as-is (no preview/convert),
 *               or @browser a live ui:{report|chart|table|icon}:@machine / web
 *               component. Download as-is; convert only on explicit ask.
 */
import { z } from "zod";

// ── machine: sci//machine:* — identity + customization, everywhere ──────
// docs:@(primitive:string)**,@(primitive:string)** — pairs; the intent / meaning channel.
export const DocsSchema = z.array(z.tuple([z.string(), z.string()]));
export const MachineSchema = z.object({
  name: z.string(),                    // machine:name
  id: z.string().optional(),           // machine:id (hash)
  label: z.string().optional(),        // machine:label — customize display
  description: z.string().optional(),  // machine:description
  value: z.string().optional(),        // machine:value
  version: z.string().optional(),      // machine:version
  tags: z.array(z.string()).optional(), // machine:tags:@(primitive:string)**
  docs: DocsSchema.optional(),         // machine:@docs** — pass any meaning through
  // sci//*:status:@status — THE MACHINE CONVENTION (Ori 2026-07-25): every
  // kind carries the same two faces — <kind>:@machine:@binary is the lifecycle
  // IDENTITY and <kind>:status:@status is this status WORD (artifact/goal/
  // activity/agent/instance/integration/sync/task). start is sender-side, complete is recipient-side, fail:start is sender
  // no-response/transport failure, fail:complete is recipient execution failure,
  // fail:block is access/policy denial, retry is an explicit retry state.
  status: z.enum(["start", "complete", "fail:start", "fail:complete", "fail:block", "retry"]).optional(),
});

// ── serial: sci//serial:index — the ordering atom ───────────────────────
export const SerialSchema = z.number().int();

// ── sync + batch — ONE announcement per send; the BATCH is its transport
// DEPTH (announcement:@machine:@batch** — the machine owns its batch list,
// exactly as temporal is depth of the node; never sibling announcements).
// sci//announcement:@sync → sync:io:@in|@out (direction), sync:total;
// sci//sync:@machine:@binary — one sync; sci//sync:status:@status — its status.
export const SyncIoSchema = z.enum(["in", "out"]);
export const SyncSchema = z.object({
  io: SyncIoSchema,                    // sync:io:@in | @out
  machine: z.string().optional(),      // sync:@machine:@binary
  total: z.number().int().optional(),  // sync:total
});
// sci//batch:@sync:@serial — each batch names its place in its sync.
export const BatchStatusSchema = z.enum(["start", "complete", "fail:start", "fail:complete", "fail:block", "retry"]);
export const BatchSchema = z.object({
  serial: SerialSchema,                // batch:@sync:@serial
  status: BatchStatusSchema,           // batch:status:@status (status:*)
});

// position: exactly one grammar address, optionally interpreted by one shape
// and backed by repeated first-class evidence refs.
export const PositionEvidenceRefSchema = z.string().min(1);
export const PositionSchema = z.object({
  path: z.string().min(1).optional(),             // shape:type:@path
  jsonpath: z.string().min(1).optional(),         // shape:type:@jsonpath
  box: z.object({
    start: z.object({ y: z.number(), x: z.number() }).strict(),
    end: z.object({ y: z.number(), x: z.number() }).strict(),
  }).strict().optional(),                         // shape:type:@box
  rowcol: z.object({
    row: z.array(z.string()).optional(),
    col: z.array(z.string()).optional(),
  }).strict().optional(),                         // shape:type:@rowcol
  interactive: z.literal(true).optional(),        // shape:type:@interactive
  shape: z.string().min(1).optional(),           // position:shape:@shape
  evidence: z.array(PositionEvidenceRefSchema).min(1).optional(), // position:evidence:@evidence**
}).strict().superRefine((position, ctx) => {
  const addressKeys = ["path", "jsonpath", "box", "rowcol", "interactive"]
    .filter((key) => position[key as keyof typeof position] !== undefined);
  if (addressKeys.length !== 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "position requires exactly one shape-family address",
    });
  }
  if (position.rowcol && !(position.rowcol.row?.length || position.rowcol.col?.length)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["rowcol"],
      message: "rowcol requires row and/or col",
    });
  }
  if (position.evidence && !position.shape) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["evidence"],
      message: "position evidence requires a shape machine",
    });
  }
  if (position.evidence && new Set(position.evidence).size !== position.evidence.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["evidence"],
      message: "position evidence refs must be unique",
    });
  }
});

// ── design: sci//design:css:@machine + mode:{dark,light}:@design** ──────
export const ModeSchema = z.enum(["dark", "light"]); // text:mode:dark | light
export const DesignSchema = z.object({
  machine: z.string().optional(),      // design:css:@machine — world-owned design id (default-by-type if absent)
  mode: ModeSchema.optional(),         // the mode to paint (else the client's current mode)
});

// ── render:@file** — file (as-is) | browser (live primitive / web component) ─
export const RenderModeSchema = z.enum(["file", "browser"]);
// sci//file:<suffix>:@(position:*) — embedded as-is, never previewed/converted.
export const FileSuffixSchema = z.enum([
  "pdf", "png", "jpg", "svg", "html", "csv", "xlsx",
  "json", "md", "txt", "js", "ts", "py", "pptx",
]);
// sci//ui:kind:{report|chart|table|icon} — browser-rendered primitives.
// Connector surfaces are NOT primitives: they are registered interactive html
// artifacts — the ONE entity crossing template (sci::artifact:causit_entity;
// the per-kind causit_surface_* templates died into it), rendered like welcome.
export const UiPrimitiveSchema = z.enum([
  "report",
  "chart",
  "table",
  "icon",
]);
// sci//interactive:{normal|hover|click|typing}:@box — live web-component states.
export const InteractiveStateSchema = z.enum(["normal", "hover", "click", "typing"]);

// One render entry = one box (one primitive at one position). The page is the LIST.
export const RenderFileSchema = z.object({
  mode: RenderModeSchema,
  // file mode (mode === "file") — embed as-is
  suffix: FileSuffixSchema.optional(),
  key: z.string().optional(),          // s3 key (download as-is)
  url: z.string().optional(),          // presigned/href
  // browser mode (mode === "browser") — ui:*:@machine / web component
  primitive: UiPrimitiveSchema.optional(),
  interactive: InteractiveStateSchema.optional(),
  // where (file-bound positions; the ref also carries position)
  position: PositionSchema.optional(),
  // how it looks — a world-owned design, default shared by type
  design: DesignSchema.optional(),
  // what this box's renderer consumes (browser mode)
  payload: z.unknown().optional(),
  // the embedded artifact's machine — carries sci//artifact:status:@status (the
  // status slot: start=sending → complete=synced | fail+retry; lifecycle is
  // sci//artifact:@machine:@binary). A user message is an
  // artifact too; its status shows here, the channel never hides it (the sync OBEYS).
  machine: MachineSchema.optional(),
});

// ── announcement facets ──────────────────────────────────────────────────
// sci//announcement:sender:@{human|workflow|agent|gate}
export const SenderSchema = z.enum(["human", "workflow", "agent", "gate"]);
// Client display kind. Announcement purpose is carried by announcement:announce:@* atoms.
export const KindSchema = z.enum(["entity", "channel", "action"]);
// sci//notify:level:* — the confirm-loop wave levels (confirm · warn · act ·
// critical · report). The retired confirmation/warning words read-tolerate
// for persisted pre-wave rows only; new rows are FIRST-CLASS notify ROWS
// (kind=notify) — the announcement body.notify hint is READ-ONLY legacy.
export const NotifyLevelSchema = z.enum([
  "confirm", "warn", "act", "critical", "report",
  "confirmation", "warning", // persisted pre-wave rows only
]);
// sci//notify:to:@human (an agent-directed notice never rides notify)
export const NotifyToSchema = z.enum(["human", "agent"]);

export const NotifySchema = z.object({
  level: NotifyLevelSchema,
  to: NotifyToSchema.optional(),
  reason: z.string().optional(),       // sci//notify:reason:@reason
});
export const PrioritizedSchema = z.object({
  active: z.boolean(),
  id: z.union([z.string(), z.number()]).optional(),
});

// sci//@ref** / position:@path — a location reference (breadcrumb + context).
export const RefSchema = z.object({
  path: z.string(),                    // position:@path (e.g. "org/entity/source/record")
  label: z.string().optional(),
  kind: z.string().optional(),         // entity | source | record | issue | action | tool | …
  position: PositionSchema.optional(), // typed WHERE address when not just a path
  machine: MachineSchema.optional(),   // the ref's machine — label/docs customization on the ref
});

// ── the announcement LANES — the announcement's OWN material slots ──────────
// THE FLATTEN (sci//announcement:{ref,artifact,map,evidence}:@**): the lanes
// ride top-level on the announcement; the `payload` wrapper word is DEAD.
// ref = explicit protocol refs; artifact = artifact shells; evidence = raw data
// evidence only; map = map claims, which may carry their own refs.
export const ArtifactPositionSlotSchema = z.object({
  position: PositionSchema,
}).strict();
export const ArtifactPayloadSchema = z.object({
  machine: MachineSchema,
  // the artifact:type family is DEAD (Ori's cleanup, 2026-07-25) — identity
  // is the shape+manifest; a persisted pre-cleanup row may still carry the
  // word, so it read-tolerates through passthrough, never a declared slot.
  suffix: z.string().optional(),
  position: z.string().optional(),
  input: z.unknown().optional(),
  slots: z.array(ArtifactPositionSlotSchema).optional(),
  params: z.array(z.unknown()).default([]),
}).passthrough();

const EvidenceProtocolKeys = ["ref", "refs", "atoms", "gate"] as const;
const hasOwn = (value: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(value, key);
const EvidenceBodySchema = z.unknown().superRefine((body, ctx) => {
  if (!body || typeof body !== "object" || Array.isArray(body)) return;
  for (const key of EvidenceProtocolKeys) {
    if (hasOwn(body as Record<string, unknown>, key)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `evidence.body must not carry protocol ${key}; use the ref/map lanes`,
      });
    }
  }
});
export const EvidencePayloadSchema = z.object({
  key: z.string().optional(),
  kind: z.string().optional(),
  confidence: z.number().optional(),
  body: EvidenceBodySchema.optional(),
}).passthrough().superRefine((value, ctx) => {
  for (const key of EvidenceProtocolKeys) {
    if (hasOwn(value, key)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `evidence must not carry protocol ${key}; use the ref/map lanes`,
      });
    }
  }
});
// THE CONFIRMATION LANE carries BOTH candidate faces (Ori's oracle notes,
// 2026-07-26): the MAP side (`candidate:map:@map**` — the HOW, the selector
// that picks the value) and the VALUE side (`candidate:{granularity,ref,
// primitive}` — the WHAT it picked, an ANSWER superseding a confirmation row
// by its `key`). Passthrough because the face decides the shape, not this
// schema; the door owns the per-face validation.
export const ConfirmationPayloadSchema = z.object({
  reason: z.string().optional(),
  ref: z.unknown().optional(),
  evidence: z.unknown().optional(),
  key: z.string().optional(),
}).passthrough();
export const AnnouncementLanesSchema = z.object({
  // a ref is protocol language: an sci atom/key string, or a SERIAL (graph.py
  // sci//serial:index:@(primitive:int) — replies reference announcements by
  // serial the same way).
  ref: z.array(z.union([z.string(), z.number().int()])).default([]),
  artifact: z.array(ArtifactPayloadSchema).default([]),
  evidence: z.array(EvidencePayloadSchema).default([]),
  confirmation: z.array(ConfirmationPayloadSchema).default([]),
}).partial().strict();

// Uniform envelope status (mirrors the integration server — NO ok flag).
// delta = interim chunk emitted to the connection (never stored on the box).
export const StatusSchema = z.enum([
  "delta", "pending_human", "pending_world", "done", "notify",
]);

// sci//announcement:announce:@error** — errors are announcement-level (first-class).
// error:@machine:code (keyed by machine + code) · error:@position** · error:@ref**
// (** = plural — positions and refs are LISTS).
export const ErrorSchema = z.object({
  machine: z.string(),                       // error:@machine:code — the error's machine (key)
  code: z.string(),                          // error:@machine:code — the code
  positions: z.array(PositionSchema).default([]), // error:@position** (where — list)
  refs: z.array(RefSchema).default([]),           // error:@ref** (what it references — list)
});

// One announcement = one stream entry; its `render` LIST is its boxes. First-class:
// every announcement carries announce:@sync (io in/out), announce:@batch (serial + status),
// and announce:@error** — the announcement IS the unit synced in batches (closes the loop).
export const AnnouncementSchema = z.object({
  id: z.string(),
  timestamp: z.string(),               // sci//announcement:@machine timestamp render field (tz-naive iso)
  conversationId: z.string().optional(), // sci//announcement:conversation:@conversation**
  trigger: z.string().optional(),      // sci//announcement:trigger:@* (auth | control | …)
  authStatus: z.string().optional(),   // sci//announcement:@status (status:start | status:complete) for an @auth flow
  machine: MachineSchema.optional(),   // sci//announcement:@machine — its identity / customization
  sync: SyncSchema.optional(),         // sci//announcement:@sync — io:@in|@out (+ machine/total)
  batch: BatchSchema.optional(),       // sci//announcement:@batch — serial + status
  error: z.array(ErrorSchema).default([]), // sci//announcement:@error** — first-class
  sender: SenderSchema,                // sci//announcement:sender:@*
  kind: KindSchema,                    // client display kind; not an oracle announcement atom
  recipient: z.array(RefSchema).default([]), // sci//announcement:recipient:@ref**
  refs: z.array(RefSchema).default([]),      // sci//announcement:ref:@ref** once solved/hydrated
  // The announcement is a reactive TREE node. World HYDRATES every ref → the full announcement:
  when: z.array(z.unknown()).default([]),    // sci//announcement:when:@subscribe** — subscriptions {machine,binary,next,interval}
  next: z.array(z.unknown()).default([]),    // sci//announcement:next:@conditional**
  reply: z.array(z.unknown()).default([]),   // sci//announcement:reply:@announcement** — what this REPLIES to
  notify: NotifySchema.optional(),     // notify projection for UI display
  prioritized: PrioritizedSchema.optional(),  // the prioritized backref (body.prioritized) for this artifact
  render: z.array(RenderFileSchema).default([]), // sci//render:@file** — the boxes
  // THE FLATTEN — the lanes are the announcement's OWN slots
  // (sci//announcement:{ref,artifact,evidence,confirmation}:@**); no payload
  // wrapper. The separate `map` lane is DEAD — the map side is a confirmation
  // face.
  // A SERVED ref lane may carry hydrated forms beside atom/serial refs.
  ref: z.array(z.unknown()).default([]),
  artifact: z.array(ArtifactPayloadSchema).default([]),
  evidence: z.array(EvidencePayloadSchema).default([]),
  confirmation: z.array(ConfirmationPayloadSchema).default([]),
  status: StatusSchema.optional(),     // stream status (delta/pending/done/notify) — orthogonal to batch.status
});

export type Docs = z.infer<typeof DocsSchema>;
export type Machine = z.infer<typeof MachineSchema>;
export type Serial = z.infer<typeof SerialSchema>;
export type SyncIo = z.infer<typeof SyncIoSchema>;
export type Sync = z.infer<typeof SyncSchema>;
export type BatchStatus = z.infer<typeof BatchStatusSchema>;
export type Batch = z.infer<typeof BatchSchema>;
export type ArtifactError = z.infer<typeof ErrorSchema>;
export type Position = z.infer<typeof PositionSchema>;
export type Mode = z.infer<typeof ModeSchema>;
export type Design = z.infer<typeof DesignSchema>;
export type RenderMode = z.infer<typeof RenderModeSchema>;
export type FileSuffix = z.infer<typeof FileSuffixSchema>;
export type UiPrimitive = z.infer<typeof UiPrimitiveSchema>;
export type InteractiveState = z.infer<typeof InteractiveStateSchema>;
export type RenderFile = z.infer<typeof RenderFileSchema>;
export type Sender = z.infer<typeof SenderSchema>;
export type Kind = z.infer<typeof KindSchema>;
export type NotifyLevel = z.infer<typeof NotifyLevelSchema>;
export type Notify = z.infer<typeof NotifySchema>;
export type Prioritized = z.infer<typeof PrioritizedSchema>;
export type Ref = z.infer<typeof RefSchema>;
export type AnnouncementLanes = z.infer<typeof AnnouncementLanesSchema>;
export type Status = z.infer<typeof StatusSchema>;
export type Announcement = z.infer<typeof AnnouncementSchema>;

// ── the box actions (every box carries them) ─────────────────────────────
// Grounded in sci//execute:* — what a box action invokes on the channel.
//   reply   → execute:channel:send  (this box as context, to its sender)
//   download→ execute:file:download (as-is; PDF convert only on explicit ask)
//   control → open the box's @ref** / what-it-used (link or modal)
//   openAuth→ an @auth announcement (trigger:auth, status:start): open the auth modal
//             for its server (the integration actor in machine/@ref), SKIPPING the catalog.
export type BoxAction =
  | "reply"
  | "download"
  | "control"
  | "retry"
  | "openAuth"
  | "prioritize"
  | "unprioritize"
  | "opened"
  | "hovered"
  | "clicked"
  | "selected";

// The integration actor an openAuth box authenticates — derived from the @auth
// announcement's machine / @ref (sci::actor:<machine>:integration), never invented.
export const serverIdOf = (a: Announcement): string | undefined => {
  const fromMachine = a.machine?.name;
  if (fromMachine) return fromMachine;
  const ref = a.refs.find((r) => /::actor:.*:integration$/.test(String((r as any)?.id ?? r ?? "")));
  const id = String((ref as any)?.id ?? ref ?? "");
  const m = id.match(/::actor:(.+):integration$/);
  return m ? m[1] : undefined;
};

// An @auth announcement (trigger:auth, still status:start = not yet complete) about an
// integration actor → the box offers `openAuth` (open the auth modal, skip the catalog).
export const isAuthAnnouncement = (a: Announcement): boolean =>
  a.trigger === "auth" && a.authStatus !== "complete" && !!serverIdOf(a);

// A delta arriving over the ws connection: a partial announcement keyed by id.
export const DeltaSchema = z.object({
  type: z.literal("delta"),
  id: z.string(),
  conversationId: z.string().optional(),
  patch: z.unknown(),                  // partial render/payload to merge into the box
});
export type Delta = z.infer<typeof DeltaSchema>;
